// =============================================================================
// features/sales/agenda/components/AppointmentDrawer.tsx — Drawer do retorno
// (doc 26 §6.1 "Drawer lateral ao selecionar uma linha, preservando a lista
// no fundo"; §7 componente `AppointmentDrawer`).
//
// Resumo do lead e contatos, últimos eventos da timeline, ações Abrir CRM /
// WhatsApp / Concluir / Reagendar / Cancelar, com formulário de conclusão
// INLINE (sem troca de página).
//
// DS: painel `--elev-5`, overlay, fade-up/slide-in, foco preso
// (useFocusTrap) com devolução ao fechar. Escape fecha SÓ quando não há
// edição pendente em nenhum dos formulários inline (guarda) — DoD do slot.
// =============================================================================

import type { Appointment } from '@elemento/shared-schemas';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import {
  inferDirection,
  isLivechatInteraction,
  livechatMessageLabel,
  parseLivechatMarker,
} from '../../../../hooks/crm/livechatTimeline';
import { maskPhone } from '../../../../hooks/crm/types';
import type { LeadInteraction } from '../../../../hooks/crm/types';
import { useLead, useLeadInteractions } from '../../../../hooks/crm/useLead';
import { cn } from '../../../../lib/cn';
import { useFocusTrap } from '../../components/useFocusTrap';
import { useAppointment } from '../hooks';
import { CHANNEL_LABELS, URGENCY_BADGE_VARIANT, URGENCY_LABEL, urgencyLevel } from '../labels';
import { formatDateTimeOrgTz } from '../timezone';

import { CancelAppointmentPanel } from './CancelAppointmentPanel';
import { CompleteAppointmentForm } from './CompleteAppointmentForm';
import { RescheduleAppointmentPanel } from './RescheduleAppointmentPanel';

type ActivePanel = 'none' | 'complete' | 'reschedule' | 'cancel';

export interface AppointmentDrawerProps {
  appointment: Appointment;
  now: Date;
  onClose: () => void;
}

/**
 * Guarda de edição do Escape (DoD do slot): só bloqueia o fechamento quando
 * há um formulário inline ABERTO com alterações não salvas. Função pura,
 * extraída para ser testável sem precisar montar o componente (o projeto
 * não tem jsdom configurado no vitest — ver nota em AgentDrawer.test.tsx).
 */
export function shouldBlockEscape(activePanel: ActivePanel, panelDirty: boolean): boolean {
  return activePanel !== 'none' && panelDirty;
}

/**
 * Rótulo exibido em "Últimos eventos" (DoD do F49-S04). `interaction.content`
 * da ponte livechat→CRM grava "[livechat] type=... ref=<uuid>" de propósito
 * (doc 17 §8.5 — o texto real da mensagem vive em `messages`, atrás do RBAC
 * do live chat, e nunca é duplicado aqui). Traduz para rótulo humano por
 * direção + tipo, reusando a mesma lógica pura do F49-S03
 * (`hooks/crm/livechatTimeline.ts`). Interação não-livechat (nota manual,
 * ligação) continua exibindo `content` normalmente — sem regressão.
 */
export function eventDisplayLabel(interaction: LeadInteraction): string {
  if (!isLivechatInteraction(interaction)) return interaction.content;
  const marker = parseLivechatMarker(interaction.content);
  if (marker === null) return interaction.content;
  return livechatMessageLabel(marker.messageType, inferDirection(interaction));
}

export function AppointmentDrawer({
  appointment,
  now,
  onClose,
}: AppointmentDrawerProps): React.JSX.Element {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [activePanel, setActivePanel] = React.useState<ActivePanel>('none');
  const [panelDirty, setPanelDirty] = React.useState(false);

  const { lead, isLoading: isLeadLoading, isError: isLeadError } = useLead(appointment.leadId);
  const { interactions, isLoading: isInteractionsLoading } = useLeadInteractions(
    appointment.leadId,
  );
  const { appointment: previousAppointment } = useAppointment(appointment.supersedesAppointmentId);

  const handleEscape = React.useCallback(() => {
    if (shouldBlockEscape(activePanel, panelDirty)) return;
    onClose();
  }, [activePanel, panelDirty, onClose]);

  useFocusTrap({ containerRef: panelRef, active: true, onEscape: handleEscape });

  const urgency = urgencyLevel(appointment, now);
  const displayName = lead?.name ?? (isLeadError ? 'Lead indisponível' : 'Carregando…');

  function backToSummary(): void {
    setPanelDirty(false);
    setActivePanel('none');
  }

  function finishPanel(): void {
    setPanelDirty(false);
    setActivePanel('none');
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-40"
      style={{ background: 'rgba(10, 18, 40, 0.40)' }}
      aria-hidden="true"
      onClick={handleEscape}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Retorno de ${displayName}`}
        className={cn(
          'fixed inset-y-0 right-0 z-50',
          'w-full max-w-[480px]',
          'flex flex-col border-l border-border overflow-hidden',
        )}
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
          animation: 'slide-in-right var(--dur-normal, 200ms) var(--ease-out) both',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b border-border-subtle shrink-0"
          style={{ background: 'var(--bg-elev-2)' }}
        >
          <div className="min-w-0">
            <h2
              className="font-display font-bold text-ink truncate"
              style={{
                fontSize: 'var(--text-xl)',
                letterSpacing: '-0.03em',
                fontVariationSettings: "'opsz' 24",
              }}
            >
              {displayName}
            </h2>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="font-mono text-xs text-ink-3">
                {formatDateTimeOrgTz(appointment.scheduledAt)}
              </p>
              {appointment.status === 'scheduled' && (
                <Badge variant={URGENCY_BADGE_VARIANT[urgency]}>{URGENCY_LABEL[urgency]}</Badge>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={handleEscape}
            aria-label="Fechar drawer do retorno"
            className={cn(
              'w-8 h-8 flex items-center justify-center rounded-sm shrink-0',
              'text-ink-3 hover:text-ink hover:bg-surface-hover transition-all duration-[150ms] ease-out',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/40',
            )}
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              className="w-4 h-4"
              aria-hidden="true"
            >
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-5">
          {isLeadLoading ? (
            <div className="animate-pulse flex flex-col gap-2">
              <div className="h-4 w-40 rounded-xs" style={{ background: 'var(--surface-muted)' }} />
              <div className="h-3 w-24 rounded-xs" style={{ background: 'var(--surface-muted)' }} />
            </div>
          ) : (
            <section aria-labelledby="drawer-lead-summary" className="flex flex-col gap-1.5">
              <h3
                id="drawer-lead-summary"
                className="font-sans text-xs font-bold uppercase tracking-[0.1em] text-ink-3"
              >
                Lead
              </h3>
              <p className="font-sans text-sm text-ink-2">
                {lead?.phone_e164 ? maskPhone(lead.phone_e164) : 'Telefone indisponível'}
              </p>
              <p className="font-sans text-sm text-ink-2">
                Canal: {CHANNEL_LABELS[appointment.channel]}
              </p>
              {appointment.reason && (
                <p className="font-sans text-sm text-ink-2">Motivo: {appointment.reason}</p>
              )}
            </section>
          )}

          {previousAppointment && (
            <div
              className="rounded-sm px-4 py-3"
              style={{ background: 'var(--bg-elev-2)', boxShadow: 'var(--elev-1)' }}
            >
              <p className="font-sans text-xs font-bold uppercase tracking-[0.08em] text-ink-3 mb-1">
                Substitui o retorno de
              </p>
              <p className="font-mono text-sm text-ink-2">
                {formatDateTimeOrgTz(previousAppointment.scheduledAt)}
              </p>
            </div>
          )}

          {/* Ações primárias — só quando não há formulário inline aberto */}
          {activePanel === 'none' && (
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-2.5">
                <Link
                  to={`/crm/${appointment.leadId}`}
                  className={cn(
                    'inline-flex items-center justify-center min-h-[40px] rounded-sm px-3',
                    'font-sans text-sm font-semibold text-azul border border-border-strong bg-surface-1',
                    'hover:border-azul hover:bg-info-bg transition-colors duration-fast',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/40',
                  )}
                >
                  Abrir no CRM
                </Link>
                <Link
                  to={`/conversas?leadId=${encodeURIComponent(appointment.leadId)}`}
                  className={cn(
                    'inline-flex items-center justify-center min-h-[40px] rounded-sm px-3',
                    'font-sans text-sm font-semibold text-verde border border-border-strong bg-surface-1',
                    'hover:border-verde hover:bg-success-bg transition-colors duration-fast',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/40',
                  )}
                >
                  WhatsApp
                </Link>
              </div>

              {appointment.status === 'scheduled' && (
                <div className="flex flex-col gap-2.5">
                  <Button variant="primary" onClick={() => setActivePanel('complete')}>
                    Concluir
                  </Button>
                  <Button variant="outline" onClick={() => setActivePanel('reschedule')}>
                    Reagendar
                  </Button>
                  <Button variant="ghost" onClick={() => setActivePanel('cancel')}>
                    Cancelar
                  </Button>
                </div>
              )}

              <section aria-labelledby="drawer-timeline" className="flex flex-col gap-2">
                <h3
                  id="drawer-timeline"
                  className="font-sans text-xs font-bold uppercase tracking-[0.1em] text-ink-3"
                >
                  Últimos eventos
                </h3>
                {isInteractionsLoading ? (
                  <p className="font-sans text-xs text-ink-4">Carregando…</p>
                ) : interactions.length === 0 ? (
                  <p className="font-sans text-xs text-ink-4">Nenhum evento registrado.</p>
                ) : (
                  <ul className="flex flex-col gap-2.5">
                    {interactions.slice(0, 5).map((event) => (
                      <li key={event.id} className="border-l-2 border-border-subtle pl-3">
                        <p className="font-sans text-xs text-ink-2">{eventDisplayLabel(event)}</p>
                        <p className="font-mono text-[10px] text-ink-4 mt-0.5">
                          {formatDateTimeOrgTz(event.createdAt)}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          )}

          {activePanel === 'complete' && (
            <CompleteAppointmentForm
              appointment={appointment}
              onDone={finishPanel}
              onCancel={backToSummary}
              onDirtyChange={setPanelDirty}
            />
          )}
          {activePanel === 'reschedule' && (
            <RescheduleAppointmentPanel
              appointment={appointment}
              onDone={finishPanel}
              onCancel={backToSummary}
              onDirtyChange={setPanelDirty}
            />
          )}
          {activePanel === 'cancel' && (
            <CancelAppointmentPanel
              appointment={appointment}
              onDone={finishPanel}
              onCancel={backToSummary}
              onDirtyChange={setPanelDirty}
            />
          )}
        </div>
      </div>
    </div>
  );
}
